import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiService } from '../api/api.service';
import { OrganizationsPortfolio } from '../../interfaces/organizations-portfolio.interface';
import {
  PortfolioCreateReport,
  PortfolioData,
} from '../../interfaces/portfolio-create-report.interface';
import { RolesEnum } from '../../enums/roles-enum';

declare const StonlyWidget;

@Injectable({
  providedIn: 'root',
})
export class UtilService {
  constructor(private api: ApiService) {}

  getCurrentOrganization(): Observable<unknown> {
    const url = 'organization/me';
    return this.api.get(url);
  }

  getCurrentIntermediary(): Observable<any> {
    const url = '/get-current-intermediary-info';
    return this.api.get(url);
  }

  public getCheckedItemsNetwork(networkId): Observable<any> {
    const url = `portfolio/intermediary-items/${networkId}`;
    return this.api.get3(url);
  }

  public saveDataToIntermediary(intermediary_id, data): Observable<any> {
    const url = `portfolio/save-intermediary-items/${intermediary_id}`;
    return this.api.post(url, data);
  }

  public getOrganizationsForPortfolio(): Observable<OrganizationsPortfolio> {
    const url = `portfolio/organizations`;
    return this.api.get3(url);
  }

  public portfolioCreateReport(
    data: PortfolioData
  ): Observable<PortfolioCreateReport> {
    const url = `portfolio/create-report`;
    return this.api.post(url, data);
  }

  public checkPortfolioTask(task_id): Observable<any> {
    const url = `portfolio/v2.5/status/${task_id}`;
    return this.api.get(url);
  }

  public abortPortfolio(data): Observable<any> {
    const url = `portfolio/abort`;
    return this.api.post(url, data);
  }

  roundHalfEven = function (num, fixed) {
    const scaledNumber = num * Math.pow(10, fixed); // from ab.cdef to abcd.ef when rounding 2 decimal places
    const scaledInteger = parseInt(scaledNumber.toString(), 10); // only abcd
    const decimalPart = scaledNumber - scaledInteger; // only .ef

    let result;
    const decimalPlaces = Math.pow(10, -fixed); // ie: 0.001 when fixed equals 3
    if (decimalPart === 0.5) {
      if (scaledInteger % 2 === 0) {
        // already even, rounding down
        result = scaledInteger * decimalPlaces;
      } else {
        // odd, go to the nearest even (rounding up)
        result = (scaledInteger + 1) * decimalPlaces;
      }
    } else if (decimalPart > 0.5) {
      //standard rounding up
      result = (scaledInteger + 1) * decimalPlaces;
    } else if (decimalPart < 0.5) {
      //standard rounding down
      result = scaledInteger * decimalPlaces;
    }

    return parseFloat(result.toFixed(fixed));
  };

  public utf8_to_b64(string) {
    return window.btoa(unescape(encodeURIComponent(string)));
  }

  getPortfolioFilters(portfolioId: number): Observable<any> {
    return this.api.get(`portfolio/v2.5/filters/${portfolioId}`);
  }

  cleanEmail(email: string): string {
    return email.trim().replace(/\s/g, '').toLowerCase();
  }

  validateEmail(email: string) {
    const re =
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(email.toLowerCase());
  }

  sendStolyWidgetEvent() {
    const userFromStorage = JSON.parse(localStorage.getItem('user'));
    const orgFromStorage = JSON.parse(localStorage.getItem('organization'));
    const networkFromStorage = JSON.parse(localStorage.getItem('intermediary'));

    const last_activity_at = userFromStorage?.last_activity_at ?? '';

    const userId = userFromStorage ? userFromStorage.id.toString() : 0;

    const role = userFromStorage ? userFromStorage.role : '';

    const isEnterAs = userFromStorage
      ? userFromStorage.role == RolesEnum.ADMINISTRATOR
      : false;

    const isNewUser = userFromStorage
      ? this.checkIfNewUser(userFromStorage._created)
      : false;

    const organizationId = orgFromStorage ? orgFromStorage.id : 0;

    const organizationname = orgFromStorage ? orgFromStorage.name : '';

    const hasPaidSubscription = orgFromStorage
      ? !!orgFromStorage.current_subscription?.paid
      : false;

    const hasSponsoredSubscription = orgFromStorage
      ? !!orgFromStorage.current_subscription?.is_sponsored
      : false;

    const networkId = networkFromStorage ? networkFromStorage.id : 0;

    const networkName = networkFromStorage ? networkFromStorage.name : '';

    StonlyWidget('identify', userId, {
      organizationid: organizationId,
      organizationname: organizationname,
      networkid: networkId,
      networkname: networkName,
      userrole: role,
      isenteras: isEnterAs,
      isnewuser: isNewUser,
      haspaidsubscription: hasPaidSubscription,
      hassponsoredsubscription: hasSponsoredSubscription,
      lastActivityAt: last_activity_at,
    });
  }

  checkIfNewUser(createdAtDate: string): boolean {
    const now = new Date();
    const created = new Date(createdAtDate);

    const diffInMs = now.getTime() - created.getTime();

    const fifteenDaysInMs = 15 * 24 * 60 * 60 * 1000;

    return diffInMs <= fifteenDaysInMs && diffInMs >= 0;
  }

  numberToEin(value: string): string {
    if (!value) return '';

    value = this.allowNumbersAndHyphen(value).trim();

    if (value.length > 10) {
      value = value.substring(0, 10);
    }

    return value;
  }

  private allowNumbersAndHyphen(ein: string): string {
    return ein.replace(/[^0-9-]+/g, '');
  }
}
